const post = {
  slug: 'observability-is-a-feature',
  number: 1,
  title: 'Observability Is a Feature, Not an Afterthought',
  date: '2026-05-28',
  readTime: '7 min read',
  views: 96,
  category: 'devops',
  tags: ['Platform', 'Reliability', 'DevOps'],
  excerpt:
    'You cannot operate what you cannot see. A practical argument for treating metrics, logs, and traces as part of the work, written from the on-call seat at 3am.',
  cover: null,
  content: `
The worst incident of my on-call life was not the outage itself. It was the forty minutes we spent not knowing what was broken. A service was failing, users were affected, and we were reading source code by the light of a pager, trying to reconstruct what the system was doing from first principles because we had not built any way to see it. We fixed it eventually. The lesson stayed: we had shipped a service we could not actually operate.

That is what bolting on observability after the fact feels like. You end up adding logs and metrics in the middle of an incident, in production, at the worst possible time, trying to instrument a fire while it burns. There is a better way, and it starts with a decision: a service is not finished until you can see what it is doing.

## The on-call test

Before anything ships, I like to ask one question, and it is a surprisingly good filter.

> If this breaks at 3am, can the person on call work out what broke and why, without reading the source code?

If the answer is no, the service is not done. It is a problem waiting for a pager to go off. This is not a high bar, and it is not about gold-plating. It just forces you to think about failure while you still have the time and the calm to design for it, instead of discovering the gaps when it is too late to fill them gracefully.

## Three things that earn their keep

**Structured logs come first.** Logs you can filter by request id, user, and operation turn a forty-minute hunt into a two-minute one. Plain text logs you have to read by eye do the opposite. The difference between a line that says "payment failed" and a structured event with the user id, the amount, the provider, and the error code is the difference between guessing and knowing.

\`\`\`text
Weak:   "User payment failed"
Strong: { event: "payment_failed", user_id: 8821, amount: 5000,
          provider: "flutterwave", error: "timeout", request_id: "a1b2" }
\`\`\`

**Then the four golden signals.** Latency, traffic, errors, and saturation, dashboarded one per service. Prometheus and Grafana make this close to free, and the entire point is to have it there before you need it, because nobody builds a dashboard calmly during an incident.

**Last, tracing across boundaries.** When a single request touches three services, a trace tells you exactly which hop ate the time. Guessing wastes everyone's evening. The first time a trace points straight at the slow dependency instead of sending you on a tour of the whole system, it has paid for the setup cost.

## Alert on symptoms, not causes

A trap I have watched good teams fall into is alerting on everything. CPU is high, page someone. A queue grew, page someone. Soon the alerts are noise, people mute them, and the one that actually mattered scrolls past unread. The cure is to alert on what users actually feel.

\`\`\`text
Page-worthy:  error rate up, requests slow, the thing is down
Not a page:   CPU high, queue depth up  (clues you look at AFTER a symptom fires)
\`\`\`

High CPU is a clue you investigate once a symptom alert wakes you, not a reason to wake up on its own. Fewer, more meaningful alerts beat a wall of noise every single time, because an alert nobody trusts is worse than no alert at all.

## Build it in, do not bolt it on

The practical version of all this is to make observability part of the definition of done, not a follow-up ticket that never gets prioritized. Before a service ships:

- Structured logs with a request id threaded through every operation.
- The four golden signals on a dashboard, one per service.
- Tracing across service boundaries for anything that makes downstream calls.
- A health endpoint that reports its dependencies, not just "ok."
- Alerts on user-facing symptoms, with the noisy cause-based ones turned off.

## The part tooling cannot fix

Observability is a habit as much as a stack. The teams that run reliable systems are the ones asking "how will we know if this misbehaves?" during design review, long before the postmortem. You cannot buy that question from a vendor, and no quantity of dashboards will save a team that never asks it.

Users feel reliability even when they never think about it. Observability is how you deliver it quietly, every single day. It is the difference between operating a system and merely hoping it stays up, and after one forty-minute scramble in the dark, I will never again ship something I cannot see.
`,
};

export default post;
